import React, { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Button, Stack, Typography } from "@mui/material";

import AlertDialog from "../../common/AlertDialog";
import OrderSummaryModal, {
  carTermPlanName,
} from "../../common/customDesignedComponents/OrderSummaryModal";
import { currencyFormatter, getObjLength, persistStoredPromoCode, clearStoredPromoCode } from "../../helpers/utils";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";

function buildStripeTotalRows({
  totalPaymentData,
  investor_id,
  hideRecordingFees,
  subAmount,
}) {
  if (!totalPaymentData) return [];

  const investorDiscountTotal =
    parseFloat(String(totalPaymentData?.discount ?? "0").replaceAll(",", "")) +
    parseFloat(
      String(totalPaymentData?.promocode_discount ?? "0").replaceAll(",", ""),
    );
  const discountAmountStr = investor_id
    ? `-${currencyFormatter(investorDiscountTotal)}`
    : `-$${totalPaymentData?.discount ?? 0}`;

  const rows = [
    {
      label: "Properties",
      value: `${totalPaymentData?.no_of_properties ?? 0} × Plan`,
      amount: `$${subAmount ?? 0}`,
    },
    {
      label: "Discount",
      value: null,
      amount: discountAmountStr,
    },
    {
      label: "Sales / Local taxes",
      value: null,
      amount: `$${totalPaymentData?.sales_tax_amount_actual ?? 0}`,
    },
  ];

  if (!hideRecordingFees) {
    rows.push({
      label: "Recording fees (deposit)",
      value: null,
      amount: `$${totalPaymentData?.recording_amount_actual ?? 0}`,
    });
  }

  rows.push({
    label: "Setup fees",
    value: null,
    amount: `$${totalPaymentData?.setup_fees_actual ?? totalPaymentData?.setup_fees ?? 0}`,
  });

  return rows;
}

const TotalPyamentPopup = ({
  dataflag = false,
  setDataFlag = () => {},
  alert = false,
  setAlert = () => {},
  planDetails,
  closePopup,
  isClose = true,
  paySecure = () => {},
  investor_id = null,
  hideRecordingFees = false,
  isTailwindComponent = false,
  showPromoCode = false,
  finalAmountRequest = null,
}) => {
  const dispatch = useDispatch();
  const {
    totalPaymentDetails,
    investorsPricingBreakdown,
    getTotalAmountLoader,
  } = useSelector((state) => state.signUpUser);

  const [promoInput, setPromoInput] = useState("");
  const [promoFeedback, setPromoFeedback] = useState({
    message: "",
    isError: false,
  });
  const [cachedPaymentData, setCachedPaymentData] = useState(null);
  const [activeRequest, setActiveRequest] = useState(finalAmountRequest);
  const promoApplyRef = useRef(false);
  const pendingPromoRef = useRef("");

  useEffect(() => {
    if (finalAmountRequest) {
      setActiveRequest(finalAmountRequest);
    }
  }, [finalAmountRequest]);

  useEffect(() => {
    if (!alert || !showPromoCode) {
      return;
    }

    const initialPromo =
      activeRequest?.data?.promo_code ||
      finalAmountRequest?.data?.promo_code ||
      localStorage.getItem("promoCode") ||
      "";
    setPromoInput(initialPromo);
    setPromoFeedback({ message: "", isError: false });
    // Only re-init when the Order Summary opens — not when activeRequest is
    // stripped after a bad promo (that would clear the inline error message).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert, showPromoCode]);

  const responsePaymentData = investor_id
    ? investorsPricingBreakdown
    : totalPaymentDetails?.data;

  useEffect(() => {
    if (!getObjLength(totalPaymentDetails)) {
      return;
    }

    if (totalPaymentDetails?.success && responsePaymentData) {
      setCachedPaymentData(responsePaymentData);
      if (promoApplyRef.current) {
        const appliedCode = pendingPromoRef.current.trim();
        if (appliedCode) {
          persistStoredPromoCode(appliedCode);
        }
        setPromoFeedback({ message: "Promo code applied.", isError: false });
        promoApplyRef.current = false;
        pendingPromoRef.current = "";
      }
      return;
    }

    if (promoApplyRef.current) {
      clearStoredPromoCode();
      pendingPromoRef.current = "";
      setActiveRequest((current) => {
        if (!current?.data) {
          return current;
        }
        const { promo_code: _removed, ...restData } = current.data;
        return { ...current, data: restData };
      });
      setPromoFeedback({
        message:
          totalPaymentDetails?.data?.message ||
          totalPaymentDetails?.message ||
          "Unable to apply promo code.",
        isError: true,
      });
      promoApplyRef.current = false;
    }
  }, [totalPaymentDetails, responsePaymentData]);

  const displayPaymentData =
    totalPaymentDetails?.success && responsePaymentData
      ? responsePaymentData
      : cachedPaymentData;

  const subAmount = investor_id
    ? displayPaymentData?.subscription_amount_actual
    : displayPaymentData?.sub_amount;

  const orderSummaryRows = buildStripeTotalRows({
    totalPaymentData: displayPaymentData,
    investor_id,
    hideRecordingFees,
    subAmount,
  });

  const planName =
    carTermPlanName(displayPaymentData) ||
    planDetails?.name ||
    planDetails?.plan_Name;
  const orderSummarySubtitle = planName ? `${planName} Plan` : undefined;

  const showOrderSummary =
    dataflag || (showPromoCode && Boolean(displayPaymentData));

  const handleApplyPromoCode = () => {
    const code = promoInput.trim();
    if (!code || !activeRequest) {
      return;
    }

    promoApplyRef.current = true;
    pendingPromoRef.current = code;
    setPromoFeedback({ message: "", isError: false });

    const nextData = { ...activeRequest.data, promo_code: code };
    const nextRequest = { ...activeRequest, data: nextData };
    setActiveRequest(nextRequest);

    dispatch(userSigupAction.getTotalPaymentDetails(nextRequest));
  };

  const clearOrderSummaryPromo = () => {
    clearStoredPromoCode();
    pendingPromoRef.current = "";
    setPromoInput("");
    setPromoFeedback({ message: "", isError: false });
    setActiveRequest((current) => {
      if (!current?.data) {
        return current;
      }
      const { promo_code: _removed, ...restData } = current.data;
      return { ...current, data: restData };
    });
  };

  const handleErrorModalClose = () => {
    clearStoredPromoCode();
    setAlert(false);
    closePopup("cancel");
  };

  return (
    <>
      {alert && showOrderSummary ? (
        <>
          {isTailwindComponent ? (
            <OrderSummaryModal
              open={alert && showOrderSummary}
              onOpenChange={(open) => {
                if (!open) {
                  setAlert(false);
                }
              }}
              preventDismiss={!isClose}
              title="ORDER SUMMARY"
              subtitle={orderSummarySubtitle}
              titleClassName="uppercase tracking-wider text-base font-bold leading-snug"
              rows={orderSummaryRows}
              totalLabel="Total due today"
              totalAmount={displayPaymentData?.total ?? "0"}
              closeButtonText="CLOSE"
              confirmButtonText="PROCEED TO PAYMENT"
              closeButtonClassName="uppercase font-bold tracking-wide text-sm"
              confirmButtonClassName="uppercase font-bold tracking-wide text-sm bg-[#1a2b48] hover:bg-[#162038] text-white hover:text-white focus-visible:ring-[#1a2b48] ring-2 ring-[#1a2b48]/35 ring-offset-2 ring-offset-background bg-primary shadow-md hover:shadow-lg"
              confirmDisabled={getTotalAmountLoader}
              showPromoCode={showPromoCode && Boolean(activeRequest)}
              promoCodeValue={promoInput}
              onPromoCodeChange={setPromoInput}
              onApplyPromoCode={handleApplyPromoCode}
              promoApplying={getTotalAmountLoader}
              promoMessage={promoFeedback.message}
              promoMessageIsError={promoFeedback.isError}
              onCloseClick={() => {
                clearOrderSummaryPromo();
                closePopup("cancel");
              }}
              onConfirm={() => {
                // paySecure first (may read totalPaymentDetails); then close Order Summary
                // so any following payment/API error modal cannot stack over it
                paySecure();
                closePopup("submit");
              }}
            />
          ) : (
            <AlertDialog
              isTailwindComponent={false}
              type="Info"
              openPopup={alert}
              onClose={isClose}
              onCloseFun={() => {
                setAlert(false);
              }}
              mesage={
                <>
                  <Typography align="center" variant="h6">
                    Total Amount Details
                    {planName ? ` for ${planName} Plan` : null}
                  </Typography>
                  <p className="para">
                    Total Properties:{" "}
                    <b>{displayPaymentData?.no_of_properties} </b>
                    <br className="d-block d-sm-none" />
                    <span className="d-none d-sm-inline-block"> and </span> Plan
                    amount:{" "}
                    <b>
                      $
                      {investor_id
                        ? displayPaymentData?.subscription_amount_actual
                        : displayPaymentData?.sub_amount}{" "}
                    </b>
                    <br />
                    Discount:{" "}
                    <b>
                      {investor_id
                        ? currencyFormatter(
                            parseFloat(
                              displayPaymentData?.discount?.replaceAll(",", ""),
                            ) +
                              parseFloat(
                                displayPaymentData?.promocode_discount?.replaceAll(
                                  ",",
                                  "",
                                ),
                              ),
                          )
                        : "$" + displayPaymentData?.discount}{" "}
                    </b>{" "}
                    <br className="d-block d-sm-none" />
                    <span className="d-none d-sm-inline-block"> and </span>{" "}
                    Sales/Local taxes:{" "}
                    <b>${displayPaymentData?.sales_tax_amount_actual} </b>
                    <>
                      <br />
                      {!hideRecordingFees && (
                        <>
                          Deposit for recording fees:{" "}
                          <b>${displayPaymentData?.recording_amount_actual} </b>{" "}
                          <br className="d-block d-sm-none" />
                          <span className="d-none d-sm-inline-block">
                            {" "}
                            and{" "}
                          </span>{" "}
                        </>
                      )}
                      Setup Fees:{" "}
                      <b>
                        $
                        {displayPaymentData?.setup_fees ??
                          displayPaymentData?.setup_fees_actual}
                      </b>
                    </>
                    <br />
                    <br className="d-block d-sm-none" />
                    Total Payable Amount: <b>${displayPaymentData?.total} </b>
                  </p>
                </>
              }
              buttons={
                <Stack spacing={2} direction={"row"} justifyContent={"center"}>
                  <Button
                    onClick={() => closePopup("cancel")}
                    type="submit"
                    className=" mx-2 popup_Btn other_popup_btn"
                    variant="contained"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      paySecure();
                      closePopup("submit");
                    }}
                    type="submit"
                    className="mx-2 popup_Btn success_popup_btn"
                    variant="contained"
                  >
                    Next
                  </Button>
                </Stack>
              }
            />
          )}
        </>
      ) : alert ? (
        <AlertDialog
          type="Error"
          isTailwindComponent={isTailwindComponent}
          openPopup={alert}
          onClose={true}
          onCloseFun={handleErrorModalClose}
          mesage={totalPaymentDetails?.data?.message}
          buttons={
            <Stack spacing={2} direction={"row"} justifyContent={"center"}>
              <Button
                className="popup_Btn error_popup_btn m-2"
                onClick={handleErrorModalClose}
              >
                cancel
              </Button>
            </Stack>
          }
        />
      ) : null}
    </>
  );
};

export default TotalPyamentPopup;
